// src/App.js
import React, { Suspense, lazy, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import { useMsal } from "@azure/msal-react";
import { persistMicrosoftTenantId } from "./auth/msalConfig";

import { useAuth } from "../providers/AuthProvider";
import useModules from "../hooks/useModules";

// ===== Layout =====
import { AppLayout, ProtectedRoute } from "../layouts";
import AuthRedirect from "./auth/AuthRedirect";

// ===== Admin =====
import {
  AdminAddOrganizationForm,
  AdminAiCredits,
  AdminDemoVideoLeads,
  AdminLandingPageArticles,
  AdminLayout,
  AdminMetrics,
  AdminOrgUsers,
} from "../features/admin";
import { AdminNotifications } from "../features/notifications";

// ===== Pages =====
import Login from "../pages/Login";
import ForgotPassword from "../pages/ForgotPassword";
import Home from "../pages/Home";
import ResetPassword from "../pages/ResetPassword";
import AcceptInvitation from "../pages/AcceptInvitation";
import Legal from "../pages/Legal";
import Privacy from "../pages/Privacy";

// ===== Shared / Core =====
import { ReportBuilder } from "../features/reports";
import { Retail } from "../features/retail";
import { CRMMetrics, Metrics } from "../features/metrics";
import { GeographicTargeting } from "../features/geographic-targeting";
import { Properties, PropertyDetails } from "../features/properties";

// ===== Shared Surfaces =====
import {
  BRECompanies as UserCompanies,
  BRECompanyDetails as CompanyDetails,
} from "../features/bre/companies";
import { BREContactDetails, BREContacts } from "../features/bre/contacts";
import { IncubatorDetails, Incubators } from "../features/bre/incubators";

// ===== BRE =====
import { BRESurveyTemplates, BRESurveys } from "../features/bre/surveys";
import { ProjectDetails, Projects } from "../features/projects";

// ===== Prospecting =====
import {
  TradeShows,
  TradeShowsLanding,
  TradeShowAgentPage,
  TradeShowRadarPage,
  UserTradeShows,
  WorkspaceDetailPage,
  TradeShowWorkspacesProvider,
} from "../features/prospecting/trade-shows";
import { WebsiteVisits } from "../features/prospecting/website-tracking";
import { SiteConsultants } from "../features/prospecting/site-consultants";
import {
  ProspectingCompanies,
  ProspectingCompanyDetails,
} from "../features/prospecting/companies";
import {
  ProspectingContactDetails,
  ProspectingContacts,
} from "../features/prospecting/contacts";
import { AiProspectingPage } from "../features/prospecting/prospect-list-generator";
import {
  MembershipDashboard,
  MembershipEvents,
  MembershipImports,
  MembershipMemberProfile,
  MembershipMembers,
  MembershipReports,
  MembershipSponsorships,
} from "../features/membership";

// ⭐ Inquiries
import { Inquiries, InquiryDetails } from "../features/prospecting/inquiries";

// ===== Lazy =====
const lazyWithChunkReload = (importer) =>
  lazy(async () => {
    try {
      const module = await importer();
      sessionStorage.removeItem("chunk-reload-attempted");
      return module;
    } catch (error) {
      const isChunkLoadError =
        error?.name === "ChunkLoadError" ||
        /Loading chunk .* failed/i.test(error?.message || "");

      if (
        isChunkLoadError &&
        !sessionStorage.getItem("chunk-reload-attempted")
      ) {
        sessionStorage.setItem("chunk-reload-attempted", "true");
        window.location.reload();
      }

      throw error;
    }
  });

const SophieCompanies = lazyWithChunkReload(
  () => import("../features/sophie-database/SophieCompanies"),
);
const OutlookHub = lazyWithChunkReload(
  () => import("../features/outlook/OutlookHub"),
);
const RetailTrends = lazyWithChunkReload(
  () => import("../features/retail/trends/RetailTrends"),
);
const FiscalImpactPage = lazyWithChunkReload(
  () => import("../components/Other/FiscalImpactPage"),
);
const BRESurveyResponses = lazyWithChunkReload(
  () => import("../features/bre/surveys/BRESurveyResponses"),
);
const BRESurveyStats = lazyWithChunkReload(
  () => import("../features/bre/surveys/BRESurveyStats"),
);
const WebsiteMetrics = lazyWithChunkReload(
  () => import("../features/prospecting/website-tracking/WebsiteMetrics"),
);
const UserSiteConsultants = lazyWithChunkReload(
  () => import("../features/prospecting/site-consultants/UserSiteConsultants"),
);

// ===== Splash =====
function Splash() {
  return (
    <div style={{ padding: 24, fontSize: 18, fontWeight: 500 }}>Loading…</div>
  );
}

function LegacyAppRedirect() {
  const location = useLocation();
  const pathname = location.pathname.replace(/^\/app(?=\/|$)/, "") || "/home";

  return (
    <Navigate
      to={`${pathname}${location.search}${location.hash}`}
      replace
    />
  );
}

function LegacyResearchRedirect() {
  const location = useLocation();
  const pathname =
    location.pathname.replace(/^\/companies(?=\/|$)/, "/research") ||
    "/research";

  return (
    <Navigate
      to={`${pathname}${location.search}${location.hash}`}
      replace
    />
  );
}

function LegacyUserCompaniesRedirect() {
  const location = useLocation();
  const pathname =
    location.pathname.replace(/^\/user-companies(?=\/|$)/, "/bre-companies") ||
    "/bre-companies";

  return (
    <Navigate
      to={`${pathname}${location.search}${location.hash}`}
      replace
    />
  );
}

function LegacyUserContactsRedirect() {
  const location = useLocation();
  const pathname =
    location.pathname.replace(/^\/user-contacts(?=\/|$)/, "/bre-contacts") ||
    "/bre-contacts";

  return (
    <Navigate
      to={`${pathname}${location.search}${location.hash}`}
      replace
    />
  );
}

// ===========================
// MAIN APP
// ===========================
export default function App() {
  const { instance } = useMsal();

  useEffect(() => {
    const accounts = instance.getAllAccounts();

    if (accounts.length > 0 && !instance.getActiveAccount()) {
      persistMicrosoftTenantId(accounts[0]);
      instance.setActiveAccount(accounts[0]);
    }
  }, [instance]);

  const { user, token, authReady } = useAuth();
  const { hasComponent } = useModules();

  const guard = (componentKey, element) =>
    hasComponent(componentKey) ? element : <Navigate to="/home" replace />;

  return (
    <Suspense fallback={<Splash />}>
      <Box sx={{ minHeight: "100vh", width: "100vw" }}>
        <Routes>
          {/* OAuth */}
          <Route path="/auth" element={<AuthRedirect />} />

          {/* Public */}
          <Route
            path="/"
            element={
              authReady ? (
                <Navigate to={user || token ? "/home" : "/login"} replace />
              ) : null
            }
          />
          <Route path="/login" element={<Login />} />
          <Route path="/admin-login" element={<Login />} />
          <Route path="/legal" element={<Legal />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/accept-invitation" element={<AcceptInvitation />} />
          <Route
            path="/survey/:templateId"
            element={<BRESurveyTemplates />}
          />

          {/* Legacy /app URLs */}
          <Route path="/app/*" element={<LegacyAppRedirect />} />

          {/* ================= ADMIN ================= */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute>
                {user?.is_admin ? (
                  <AdminLayout />
                ) : (
                  <Navigate to="/home" replace />
                )}
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="create-org" replace />} />
            <Route path="create-org" element={<AdminAddOrganizationForm />} />
            <Route path="org-users" element={<AdminOrgUsers />} />
            <Route path="metrics" element={<AdminMetrics />} />
            <Route path="ai-credits" element={<AdminAiCredits />} />
            <Route path="notifications" element={<AdminNotifications />} />
            <Route path="demo-leads" element={<AdminDemoVideoLeads />} />
            <Route path="articles" element={<AdminLandingPageArticles />} />
          </Route>

          {/* ================= CLIENT APP ================= */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            {/* Home */}
            <Route path="home" element={<Home />} />

            {/* Core */}
            <Route
              path="outlook/*"
              element={guard("email.outlook", <OutlookHub />)}
            />
            <Route path="metrics" element={<Metrics />} />
            <Route path="metrics/crm" element={<CRMMetrics />} />
            <Route path="reports/builder" element={<ReportBuilder />} />
            <Route
              path="fiscal-impact"
              element={guard("other.fiscal_impact", <FiscalImpactPage />)}
            />
            <Route
              path="retail"
              element={guard("other.retail", <Retail />)}
            />
            <Route
              path="geographic-targeting"
              element={
                guard("other.geographic-targeting", <GeographicTargeting />)
              }
            />
            <Route
              path="retail/trends"
              element={guard("other.retail", <RetailTrends />)}
            />

            {/* Projects */}
            <Route path="projects" element={<Projects />} />
            <Route path="projects/new" element={<ProjectDetails />} />
            <Route path="projects/:id" element={<ProjectDetails />} />
            <Route
              path="properties"
              element={guard("other.property_management", <Properties />)}
            />
            <Route
              path="properties/:id"
              element={guard("other.property_management", <PropertyDetails />)}
            />

            {/* Research */}
            <Route path="companies/*" element={<LegacyResearchRedirect />} />
            <Route
              path="research"
              element={
                <Suspense fallback={<Splash />}>
                  <SophieCompanies />
                </Suspense>
              }
            />

            {/* Shared Surfaces */}
            <Route
              path="user-companies/*"
              element={<LegacyUserCompaniesRedirect />}
            />
            <Route
              path="bre-companies"
              element={guard("bre.companies", <UserCompanies />)}
            />
            <Route
              path="bre-companies/new"
              element={guard("bre.companies", <CompanyDetails />)}
            />
            <Route
              path="bre-companies/:id"
              element={guard("bre.companies", <CompanyDetails />)}
            />
            <Route
              path="membership"
              element={guard("bre.membership", <MembershipDashboard />)}
            />
            <Route
              path="membership/members"
              element={guard("bre.membership", <MembershipMembers />)}
            />
            <Route
              path="membership/members/new"
              element={guard("bre.membership", <MembershipMembers />)}
            />
            <Route
              path="membership/members/:id"
              element={guard("bre.membership", <MembershipMemberProfile />)}
            />
            <Route
              path="membership/events"
              element={guard("bre.membership", <MembershipEvents />)}
            />
            <Route
              path="membership/events/:id"
              element={guard("bre.membership", <MembershipEvents />)}
            />
            <Route
              path="membership/sponsorships"
              element={guard("bre.membership", <MembershipSponsorships />)}
            />
            <Route
              path="membership/sponsorships/:id"
              element={guard("bre.membership", <MembershipSponsorships />)}
            />
            <Route
              path="membership/reports"
              element={guard("bre.membership", <MembershipReports />)}
            />
            <Route
              path="membership/imports"
              element={guard("bre.membership", <MembershipImports />)}
            />

            <Route
              path="user-contacts/*"
              element={<LegacyUserContactsRedirect />}
            />
            <Route
              path="bre-contacts"
              element={guard("bre.contacts", <BREContacts />)}
            />
            <Route
              path="bre-contacts/new"
              element={guard("bre.contacts", <BREContactDetails />)}
            />
            <Route
              path="bre-contacts/:id"
              element={guard("bre.contacts", <BREContactDetails />)}
            />

            <Route
              path="incubators"
              element={guard("bre.incubators", <Incubators />)}
            />
            <Route
              path="incubators/new"
              element={guard("bre.incubators", <IncubatorDetails />)}
            />
            <Route
              path="incubators/:id"
              element={guard("bre.incubators", <IncubatorDetails />)}
            />

            {/* ================= BRE ================= */}
            <Route
              path="surveys"
              element={guard("bre.surveys", <BRESurveys />)}
            />
            <Route
              path="survey-templates"
              element={guard("bre.surveys", <BRESurveyTemplates />)}
            />
            <Route
              path="survey-responses"
              element={guard("bre.surveys", <BRESurveyResponses />)}
            />
            <Route
              path="survey-stats"
              element={guard("bre.surveys", <BRESurveyStats />)}
            />

            {/* ================= PROSPECTING ================= */}

            <Route
              path="ai-prospecting"
              element={guard("prospecting.weekly_prospects", <AiProspectingPage />)}
            />

            {/* ⭐ Inquiries */}
            <Route
              path="inquiries"
              element={guard("prospecting.inquiries", <Inquiries />)}
            />

            <Route
              path="inquiries/new"
              element={guard("prospecting.inquiries", <InquiryDetails />)}
            />

            <Route
              path="inquiries/:id"
              element={guard("prospecting.inquiries", <InquiryDetails />)}
            />

            {/* Prospecting Companies */}
            <Route
              path="prospecting-companies"
              element={guard("prospecting.companies", <ProspectingCompanies />)}
            />

            <Route
              path="prospecting-companies/new"
              element={
                guard("prospecting.companies", <ProspectingCompanyDetails />)
              }
            />

            <Route
              path="prospecting-companies/:id"
              element={
                guard("prospecting.companies", <ProspectingCompanyDetails />)
              }
            />

            {/* Prospecting Contacts */}
            <Route
              path="prospecting-contacts"
              element={guard("prospecting.contacts", <ProspectingContacts />)}
            />

            <Route
              path="prospecting-contacts/new"
              element={
                guard("prospecting.contacts", <ProspectingContactDetails />)
              }
            />

            <Route
              path="prospecting-contacts/:id"
              element={
                guard("prospecting.contacts", <ProspectingContactDetails />)
              }
            />

            {/* Trade Shows */}
            <Route
              path="tradeshows"
              element={guard("prospecting.trade_shows", <TradeShowsLanding />)}
            />

            <Route
              path="tradeshows/database"
              element={guard("prospecting.trade_shows", <TradeShows />)}
            />

            <Route
              path="trade-show-radar"
              element={guard("prospecting.trade_shows", <TradeShowRadarPage />)}
            />

            <Route
              path="my-trade-shows"
              element={guard("prospecting.trade_shows", <UserTradeShows />)}
            />

            <Route
              path="trade-show-agent"
              element={guard(
                "prospecting.trade_show_agent",
                <TradeShowWorkspacesProvider>
                  <TradeShowAgentPage />
                </TradeShowWorkspacesProvider>,
              )}
            />

            <Route
              path="trade-show-agent/workspaces/:id"
              element={guard(
                "prospecting.trade_show_agent",
                <TradeShowWorkspacesProvider>
                  <WorkspaceDetailPage />
                </TradeShowWorkspacesProvider>,
              )}
            />

            {/* Website Visits */}
            <Route
              path="website-visits"
              element={guard("prospecting.website_visits", <WebsiteVisits />)}
            />

            <Route
              path="website-metrics"
              element={guard("prospecting.website_visits", <WebsiteMetrics />)}
            />

            {/* Site Consultants */}
            <Route
              path="site-consultants"
              element={guard("prospecting.site_consultants", <SiteConsultants />)}
            />

            <Route
              path="my-site-consultants"
              element={
                guard("prospecting.site_consultants", <UserSiteConsultants />)
              }
            />

          </Route>

          {/* Global fallback */}
          <Route
            path="*"
            element={
              authReady ? (
                user || token ? (
                  <Navigate to="/home" replace />
                ) : (
                  <Navigate to="/" replace />
                )
              ) : (
                <Splash />
              )
            }
          />
        </Routes>
      </Box>
    </Suspense>
  );
}
